import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import { Activity, GraduationCap, Layers, UsersRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchAnalyticsSummary } from "../api";
import { buildFiltersKey } from "../utils";
import type { AnalyticsFiltersState, AnalyticsSummary } from "../types";

interface AnalyticsHeroCardsProps {
  filters: AnalyticsFiltersState;
}

const HERO_VARIANTS = [
  {
    gradient: "from-sky-500 via-cyan-500 to-sky-400",
    iconColor: "text-white",
    shadow: "shadow-[0_20px_45px_-18px_rgba(14,165,233,0.7)]",
  },
  {
    gradient: "from-indigo-500 via-purple-500 to-indigo-400",
    iconColor: "text-white",
    shadow: "shadow-[0_20px_45px_-18px_rgba(99,102,241,0.7)]",
  },
  {
    gradient: "from-emerald-500 via-green-500 to-emerald-400",
    iconColor: "text-white",
    shadow: "shadow-[0_20px_45px_-18px_rgba(16,185,129,0.7)]",
  },
  {
    gradient: "from-amber-500 via-orange-500 to-amber-400",
    iconColor: "text-white",
    shadow: "shadow-[0_20px_45px_-18px_rgba(249,115,22,0.7)]",
  },
] as const;

export function AnalyticsHeroCards({ filters }: AnalyticsHeroCardsProps) {
  const windowDays = useMemo(() => {
    if (filters.dateRange.from && filters.dateRange.to) {
      const fromDate = new Date(`${filters.dateRange.from}T00:00:00`);
      const toDate = new Date(`${filters.dateRange.to}T00:00:00`);
      if (
        !Number.isNaN(fromDate.getTime()) &&
        !Number.isNaN(toDate.getTime())
      ) {
        const diffMs = toDate.getTime() - fromDate.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
        return Math.max(1, diffDays);
      }
    }
    return 30;
  }, [filters.dateRange.from, filters.dateRange.to]);

  const { data: summary, isLoading } = useQuery<AnalyticsSummary>({
    queryKey: ["analytics", "summary", buildFiltersKey(filters)],
    queryFn: () =>
      fetchAnalyticsSummary({
        windowDays,
        clienteIntermedioId: filters.clienteIntermedioId,
        clienteFinalId: filters.clienteFinalId,
        programaId: filters.programaId,
        actividadId: filters.actividadId,
        dateFrom: filters.dateRange.from ?? undefined,
        dateTo: filters.dateRange.to ?? undefined,
      }),
  });

  const heroCards = useMemo(
    () => [
      {
        title: "Clientes intermedios",
        value: summary?.entidades.clientesIntermedios ?? null,
        icon: <Layers className="h-7 w-7" />,
        variant: HERO_VARIANTS[0],
      },
      {
        title: "Clientes finales",
        value: summary?.entidades.clientesFinales ?? null,
        icon: <UsersRound className="h-7 w-7" />,
        variant: HERO_VARIANTS[1],
      },
      {
        title: "Programas",
        value: summary?.entidades.programas ?? null,
        icon: <Activity className="h-7 w-7" />,
        variant: HERO_VARIANTS[2],
      },
      {
        title: "Aprendices",
        value: summary?.entidades.aprendices ?? null,
        icon: <GraduationCap className="h-7 w-7" />,
        variant: HERO_VARIANTS[3],
      },
    ],
    [summary],
  );

  return (
    <section className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      {heroCards.map((card) => (
        <HeroCard
          key={card.title}
          title={card.title}
          value={card.value}
          loading={isLoading}
          icon={card.icon}
          variant={card.variant}
        />
      ))}
    </section>
  );
}


function HeroCard({
  title,
  value,
  loading,
  icon,
  variant,
}: {
  title: string;
  value: number | null;
  loading: boolean;
  icon: React.ReactNode;
  variant?: {
    gradient: string;
    iconColor: string;
    shadow: string;
  };
}) {
  return (
    <Card
      className={cn(
        "relative overflow-hidden border-0 text-white transition-transform duration-200 hover:-translate-y-1",
        variant?.shadow,
      )}
    >
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-br",
          variant?.gradient ?? "from-slate-700 via-slate-600 to-slate-700",
        )}
      />
      <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium uppercase tracking-wide text-white/80">
          {title}
        </CardTitle>
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/15 backdrop-blur">
          <span className={variant?.iconColor ?? "text-white"}>{icon}</span>
        </span>
      </CardHeader>
      <CardContent className="relative">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-24 bg-white/40" />
            <Skeleton className="h-3 w-16 bg-white/30" />
          </div>
        ) : (
          <>
            <div className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
              {typeof value === "number" ? value.toLocaleString() : "—"}
            </div>
            <p className="mt-2 text-xs text-white/70">
              Indicador calculado con base en el periodo seleccionado.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
